/**
 * Temporal Planner - Step 0+1 Implementation
 * Wraps placement algorithm with time bitmap management
 *
 * Provides two interfaces:
 * - planParkingAssignments: Full temporal planning over time horizon
 * - planSnapshotPlacement: Simplified API for current ramp state (single moment)
 */

import { getDecisionLog, parkingAlgorithm } from "./parkingAlgorithm";
import { SortingStrategy } from "./sortingStrategies";
import { ParkingRequest, PlanningResult, Spot, Zone } from "./types";

/**
 * Plan parking assignments for a set of parking requests over a time horizon
 * Implements Step 0+1: Basic temporal planning with time bitmaps and segmented dwells
 *
 * @param requests - List of parking requests to place
 * @param spots - List of available spots (modified in place - bitmaps updated by algorithm)
 * @param bufferMinutes - Optional buffer to add before/after dwell times (default: 15)
 * @param plannedMoveCap - Maximum number of planned moves per aircraft (default: 0 = no segmentation)
 * @param allowTrapping - Allow placements that trap other aircraft (default: true = soft penalty mode)
 * @param egressWeight - Weight multiplier for trapped aircraft in scoring (default: 1.0, range: 1.0=low, 2.0=medium, 5.0=high)
 * @param wasteWeight - Weight multiplier for spot size waste in scoring (default: 1.0, range: 0=disabled, 0.5-3.0=increasing penalty)
 * @param structuralWeight - Weight multiplier for structural complexity penalty (default: 0.1, range: 0=disabled, 0.05-0.5=tiebreaker)
 * @param sortingStrategy - Strategy for ordering parking requests (default: 'arrival')
 * @param horizonHours - Planning horizon in hours (default: 24)
 * @returns Planning result with placement map and stats (includes timing information)
 */
export function planParkingAssignments(
  requests: ParkingRequest[],
  spots: Spot[],
  bufferMinutes: number = 15,
  plannedMoveCap: number = 0,
  allowTrapping: boolean = true,
  egressWeight: number = 1.0,
  wasteWeight: number = 1.0,
  structuralWeight: number = 0.1,
  sortingStrategy: SortingStrategy = "arrival",
  horizonHours: number = 24,
  // shapes parameter REMOVED - use spot.shape instead
  useMaskOptimization: boolean = false,
  validateMasks: boolean = false,
  zonePriorityWeight: number = 1.0,
  zones: Zone[] = [],
  min_dwell_weight: number = 2.0
): PlanningResult {
  console.log(
    `[TemporalPlanner] Starting placement for ${requests.length} requests on ${spots.length} spots`
  );
  console.log(
    `[TemporalPlanner] Buffer: ${bufferMinutes}min, Move cap: ${plannedMoveCap}, Allow trapping: ${allowTrapping}, Egress weight: ${egressWeight}x, Waste weight: ${wasteWeight}x, Structural weight: ${structuralWeight}x, Sorting: ${sortingStrategy}`
  );
  if (useMaskOptimization) {
    console.log(
      `[TemporalPlanner] Phase 9: Mask optimization enabled (validate: ${validateMasks})`
    );
  }

  // Call placement algorithm
  // The algorithm checks time availability using spot.occupancy bitmaps
  // and reserves spots as it makes placements
  const placement = parkingAlgorithm(
    requests,
    spots,
    bufferMinutes,
    plannedMoveCap,
    allowTrapping,
    egressWeight,
    wasteWeight,
    structuralWeight,
    sortingStrategy,
    horizonHours,
    // shapes parameter removed - using spot.shape
    useMaskOptimization,
    validateMasks,
    zonePriorityWeight,
    zones,
    min_dwell_weight
  );

  // Gather stats
  const placedIds = new Set(Object.keys(placement));
  const unplacedAircraftIds = requests
    .filter((req) => !placedIds.has(req.id))
    .map((req) => req.id);

  // Count segmented placements (aircraft with multiple segments)
  let plannedMoves = 0;
  for (const aircraftId in placement) {
    const p = placement[aircraftId];
    if ("segments" in p && p.segments.length > 1) {
      plannedMoves++;
    }
  }

  const stats = {
    totalAircraft: requests.length,
    placed: placedIds.size,
    unplaced: unplacedAircraftIds.length,
    totalMovements: 0, // unused in v1
    plannedMoves,
  };

  return {
    placement,
    stats,
    unplacedAircraftIds,
    log: getDecisionLog(),
  };
}

/**
 * Plan parking for current ramp state (snapshot at single moment in time)
 *
 * Simplified wrapper around temporal algorithm for "current ramp" use cases.
 * All aircraft are placed at the same time (bucket 0), eliminating time conflicts.
 *
 * This is useful for:
 * - Current ramp visualization
 * - Manual rearrangement scenarios
 * - Initial placement without time dimension
 *
 * @param requests - Parking requests (arrivalBucket/departureBucket will be overridden to bucket 0→1)
 * @param spots - Available spots
 * @param allowTrapping - Allow placements that trap aircraft (default: false for snapshot)
 * @param egressWeight - Egress penalty weight (default: 1.0, range: 1.0=low, 5.0=high)
 * @param wasteWeight - Spot size waste penalty weight (default: 1.0, range: 0=disabled, 3.0=high)
 * @param structuralWeight - Structural complexity penalty weight (default: 0.1, range: 0=disabled, 0.5=high)
 * @param sortingStrategy - How to order aircraft (default: 'size-constrained' for snapshot)
 * @returns Planning result with placement map and stats
 *
 * @example
 * ```typescript
 * // Place all aircraft on current ramp
 * const result = planSnapshotPlacement(
 *   currentAircraft,
 *   spots,
 *   false,  // Don't allow trapping
 *   1.0,    // Standard egress weight
 *   1.0,    // Standard waste weight
 *   0.1     // Standard structural weight
 * );
 * ```
 */
export function planSnapshotPlacement(
  requests: ParkingRequest[],
  spots: Spot[],
  allowTrapping: boolean = false,
  egressWeight: number = 1.0,
  wasteWeight: number = 1.0,
  structuralWeight: number = 0.1,
  sortingStrategy: SortingStrategy = "size-constrained",
  zonePriorityWeight: number = 1.0,
  zones: Zone[] = []
): PlanningResult {
  // Convert all requests to snapshot mode (all at bucket 0)
  // This eliminates time conflicts - all aircraft exist at same moment
  const snapshotRequests: ParkingRequest[] = requests.map((req) => ({
    ...req,
    arrivalBucket: 0,
    departureBucket: 1, // Single bucket [0, 1) represents the snapshot moment
  }));

  // Call full temporal planner with snapshot-appropriate settings
  return planParkingAssignments(
    snapshotRequests,
    spots,
    0, // bufferMinutes: no buffers for snapshot
    0, // plannedMoveCap: no segmentation for snapshot
    allowTrapping,
    egressWeight,
    wasteWeight,
    structuralWeight,
    sortingStrategy,
    0.25, // horizonHours: 1 bucket = 15 minutes (0.25 hours) is sufficient for snapshot
    // shapes parameter removed
    false, // useMaskOptimization: not needed for snapshot
    false, // validateMasks: not needed for snapshot
    zonePriorityWeight,
    zones
  );
}
